import { Badge, Box, Icon, Text, type Color, type IconName } from "@salestrak/ui";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import {
  FORM_PROPERTY_FIELD_TYPE,
  FORM_PROPERTY_LABEL,
  FORM_QUESTION_LABEL,
  questionKindForFieldType,
  type FormItem,
  type FormProperty,
} from "#app/lib/forms";

export function formPropertyIcon(property: FormProperty): IconName {
  switch (property) {
    case "address":
      return PROPERTY_ICON.address;
    case "assignees":
      return PROPERTY_ICON.assignee;
    case "issueSource":
      return PROPERTY_ICON.issueSource;
    case "issueType":
      return PROPERTY_ICON.issueType;
    case "stage":
      return PROPERTY_ICON.stage;
    case "teams":
      return PROPERTY_ICON.team;
    case "workflow":
      return PROPERTY_ICON.workflow;
    case "name":
      return "type";
    case "description":
      return "align-left";
  }
}

type FormItemGlyph = {
  icon: IconName;
  color: Color;
  kindLabel: string;
  /** Null when the answer stays on the submission or the link is broken. */
  linkedTo: { name: string; icon: IconName } | null;
  linkBroken: boolean;
};

export function formItemGlyph(
  item: FormItem,
  customFieldsById: ReadonlyMap<string, CustomFieldDef>,
): FormItemGlyph {
  if (item.kind === "section") {
    return {
      color: "dark-grey",
      icon: "heading-2",
      kindLabel: "Section",
      linkBroken: false,
      linkedTo: null,
    };
  }
  if (item.kind === "text") {
    return {
      color: "dark-grey",
      icon: "text",
      kindLabel: "Text",
      linkBroken: false,
      linkedTo: null,
    };
  }
  if (item.kind === "property") {
    const type = FORM_PROPERTY_FIELD_TYPE[item.property];
    const meta = getFieldTypeMeta(type);
    return {
      color: meta.color,
      icon: meta.icon,
      kindLabel: shapeLabel(type),
      linkBroken: false,
      linkedTo: {
        icon: formPropertyIcon(item.property),
        name: FORM_PROPERTY_LABEL[item.property],
      },
    };
  }
  if (item.kind === "customField") {
    const field = customFieldsById.get(item.customFieldId);
    if (!field) {
      // The item keeps its asked shape, so a deleted field costs only its destination.
      const meta = item.fieldType ? getFieldTypeMeta(item.fieldType) : null;
      return {
        color: meta?.color ?? "destructive",
        icon: meta?.icon ?? "alert-circle",
        kindLabel: item.fieldType ? shapeLabel(item.fieldType) : "Question",
        linkBroken: true,
        linkedTo: null,
      };
    }
    const meta = getFieldTypeMeta(field.type);
    return {
      color: meta.color,
      icon: meta.icon,
      kindLabel: shapeLabel(field.type),
      linkBroken: false,
      linkedTo: { icon: "link", name: field.name },
    };
  }
  const meta = getFieldTypeMeta(item.field.type);
  return {
    color: meta.color,
    icon: meta.icon,
    kindLabel: shapeLabel(item.field.type),
    linkBroken: false,
    linkedTo: null,
  };
}

/** Only the canvas card renders it; the settings pane states the link in full. */
export function ItemDestination({ glyph }: { glyph: FormItemGlyph }) {
  if (glyph.linkBroken) {
    return (
      <Badge color="destructive" tone="soft">
        <Icon name="unlink" size="xs" />
        Link broken
      </Badge>
    );
  }
  if (!glyph.linkedTo) return null;
  return (
    // A field name has no length bound, so it truncates first; the tooltip returns it.
    <Box align="center" direction="row" gap="xs" minWidth={0} shrink>
      <Icon color="subtle" name={glyph.linkedTo.icon} size="xs" />
      <Text overflowTooltip size="xs" truncate variant="muted">
        {glyph.linkedTo.name}
      </Text>
    </Box>
  );
}

function shapeLabel(type: CustomFieldDef["type"]): string {
  const kind = questionKindForFieldType(type);
  if (kind === "attachment") return "Photos";
  return kind ? FORM_QUESTION_LABEL[kind] : getFieldTypeMeta(type).label;
}
